import React, { useMemo, useState } from 'react';
import { X, Star, GitMerge, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { MatchRecord, Player, StatRecord, GameState } from '../types';

// ── Types ──────────────────────────────────────────────────────────────────

type StatCategory = 'serves' | 'reception' | 'attack' | 'defense' | 'setting';

interface PlayerCategoryStats {
  aces: number;
  serveErrors: number;
  serveAttempts: number;
  receptions: number;
  receptionErrors: number;
  receptionSum: number;
  kills: number;
  attackErrors: number;
  attackAttempts: number;
  digs: number;
  digErrors: number;
  blockSolos: number;
  blockAssists: number;
  assists: number;
}

interface AggregatedRecord {
  label: string;
  source: string;
  date: string;
  statCount: number;
  players: Record<string, PlayerCategoryStats>;
  score: { ourScore: number; theirScore: number; ourSetsWon: number; theirSetsWon: number };
}

interface RecordDiffViewerProps {
  recordA: MatchRecord;
  recordB: MatchRecord;
  players: Player[];
  currentOrgId: string;
  currentUserId: string;
  currentUserName: string;
  onSaveMerged: (merged: GameState) => void;
  onClose: () => void;
  /** Pre-fill source selections when re-opening a saved merge for review */
  initialSources?: Record<string, 'a' | 'b' | 'both'>;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const EMPTY_PLAYER_STATS = (): PlayerCategoryStats => ({
  aces: 0, serveErrors: 0, serveAttempts: 0,
  receptions: 0, receptionErrors: 0, receptionSum: 0,
  kills: 0, attackErrors: 0, attackAttempts: 0,
  digs: 0, digErrors: 0, blockSolos: 0, blockAssists: 0,
  assists: 0,
});

function aggregate(record: MatchRecord): AggregatedRecord {
  const stats = record.gameState.stats ?? [];
  const players: Record<string, PlayerCategoryStats> = {};

  for (const s of stats) {
    if (!players[s.playerId]) players[s.playerId] = EMPTY_PLAYER_STATS();
    const p = players[s.playerId];
    switch (s.statType) {
      case 'ace':            p.aces++; p.serveAttempts++; break;
      case 'serve_error':    p.serveErrors++; p.serveAttempts++; break;
      case 'serve_attempt':  p.serveAttempts++; break;
      case 'reception':      p.receptions++; if (s.passRating != null) p.receptionSum += s.passRating; break;
      case 'reception_error':p.receptionErrors++; p.receptions++; break;
      case 'kill':           p.kills++; p.attackAttempts++; break;
      case 'attack_error':   p.attackErrors++; p.attackAttempts++; break;
      case 'attack_attempt': p.attackAttempts++; break;
      case 'dig':            p.digs++; break;
      case 'dig_error':      p.digErrors++; break;
      case 'block_solo':     p.blockSolos++; break;
      case 'block_assist':   p.blockAssists++; break;
      case 'assist':         p.assists++; break;
    }
  }

  const src = stats.some(s => s.source === 'live') ? 'Live'
    : stats.some(s => s.source === 'import') ? 'Import'
    : stats.some(s => s.source === 'manual') ? 'Manual' : 'Record';

  return {
    label: src,
    source: src,
    date: new Date(record.submittedAt).toLocaleDateString(),
    statCount: stats.length,
    players,
    score: {
      ourScore: record.gameState.ourScore ?? 0,
      theirScore: record.gameState.theirScore ?? 0,
      ourSetsWon: record.gameState.ourSetsWon ?? 0,
      theirSetsWon: record.gameState.theirSetsWon ?? 0,
    },
  };
}

// Stats from a specific category
const CATEGORY_STAT_TYPES: Record<StatCategory, StatRecord['statType'][]> = {
  serves:    ['ace', 'serve_error', 'serve_attempt'],
  reception: ['reception', 'reception_error'],
  attack:    ['kill', 'attack_error', 'attack_attempt'],
  defense:   ['dig', 'dig_error', 'block_solo', 'block_assist'],
  setting:   ['assist'],
};

const CATEGORY_LABELS: Record<StatCategory, string> = {
  serves: 'Serves', reception: 'Reception', attack: 'Attack', defense: 'Defense', setting: 'Setting',
};

function deltaClass(a: number, b: number): string {
  if (a === b) return 'text-slate-400';
  const diff = Math.abs(a - b);
  const base = Math.max(a, b);
  const pct = base > 0 ? diff / base : 1;
  if (pct < 0.15) return 'text-amber-500';
  return 'text-red-500';
}

function DeltaBadge({ a, b }: { a: number; b: number }) {
  if (a === b) return <span className="text-slate-300 text-[9px]">—</span>;
  const diff = a - b;
  return (
    <span className={`text-[9px] font-bold ${diff > 0 ? 'text-blue-500' : 'text-orange-500'}`}>
      {diff > 0 ? `+${diff}` : `${diff}`}
    </span>
  );
}

// Summary row values for a category
function categorySummary(p: PlayerCategoryStats, cat: StatCategory): { label: string; value: number }[] {
  switch (cat) {
    case 'serves':    return [{ label: 'ACE', value: p.aces }, { label: 'ERR', value: p.serveErrors }, { label: 'ATT', value: p.serveAttempts }];
    case 'reception': return [{ label: 'REC', value: p.receptions }, { label: 'ERR', value: p.receptionErrors }, { label: 'AVG', value: p.receptions > 0 ? Math.round((p.receptionSum / p.receptions) * 10) / 10 : 0 }];
    case 'attack':    return [{ label: 'K', value: p.kills }, { label: 'ERR', value: p.attackErrors }, { label: 'ATT', value: p.attackAttempts }];
    case 'defense':   return [{ label: 'DIG', value: p.digs }, { label: 'ERR', value: p.digErrors }, { label: 'BLK', value: p.blockSolos + p.blockAssists }];
    case 'setting':   return [{ label: 'AST', value: p.assists }];
  }
}

function categoryHasData(rec: AggregatedRecord, cat: StatCategory): boolean {
  return Object.values(rec.players).some(p => {
    const vals = categorySummary(p, cat);
    return vals.some(v => v.value > 0);
  });
}

// ── Component ──────────────────────────────────────────────────────────────

export default function RecordDiffViewer({
  recordA, recordB, players, onSaveMerged, onClose, initialSources,
}: RecordDiffViewerProps) {
  const aggA = useMemo(() => aggregate(recordA), [recordA]);
  const aggB = useMemo(() => aggregate(recordB), [recordB]);

  // Per-category source selection: 'a' | 'b' | 'both'
  const [sources, setSources] = useState<Record<StatCategory, 'a' | 'b' | 'both'>>(() => ({
    serves: (initialSources?.serves as 'a' | 'b' | 'both') ?? 'a',
    reception: (initialSources?.reception as 'a' | 'b' | 'both') ?? 'a',
    attack: (initialSources?.attack as 'a' | 'b' | 'both') ?? 'a',
    defense: (initialSources?.defense as 'a' | 'b' | 'both') ?? 'a',
    setting: (initialSources?.setting as 'a' | 'b' | 'both') ?? 'a',
  }));
  const [expandedCats, setExpandedCats] = useState<Set<StatCategory>>(new Set(['serves', 'reception', 'attack']));
  const [saving, setSaving] = useState(false);

  // All player IDs mentioned across both records
  const allPlayerIds = useMemo(() => {
    const ids = new Set([...Object.keys(aggA.players), ...Object.keys(aggB.players)]);
    return Array.from(ids).filter(id => id && id !== 'team');
  }, [aggA, aggB]);

  const playerName = (id: string) => players.find(p => p.id === id)?.name ?? id.slice(0, 6);

  // Active categories (at least one record has data)
  const activeCategories = (Object.keys(CATEGORY_LABELS) as StatCategory[]).filter(
    cat => categoryHasData(aggA, cat) || categoryHasData(aggB, cat)
  );

  function toggleExpand(cat: StatCategory) {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }

  function setSource(cat: StatCategory, src: 'a' | 'b' | 'both') {
    setSources(prev => ({ ...prev, [cat]: src }));
  }

  // Score alignment
  const scoreMatch = aggA.score.ourScore === aggB.score.ourScore
    && aggA.score.theirScore === aggB.score.theirScore
    && aggA.score.ourSetsWon === aggB.score.ourSetsWon
    && aggA.score.theirSetsWon === aggB.score.theirSetsWon;

  function buildMerged() {
    const statsA = recordA.gameState.stats ?? [];
    const statsB = recordB.gameState.stats ?? [];

    const kept: StatRecord[] = [];
    // Keep internal tracking stats from A (break_point, server_point, etc.) by default
    const internalTypes: StatRecord['statType'][] = ['break_point', 'rotation_point', 'server_point'];
    for (const s of statsA) {
      if (internalTypes.includes(s.statType)) kept.push(s);
    }

    for (const cat of activeCategories) {
      const types = CATEGORY_STAT_TYPES[cat];
      const src = sources[cat];
      if (src === 'a' || src === 'both') {
        for (const s of statsA) if (types.includes(s.statType as any)) kept.push(s);
      }
      if (src === 'b' || src === 'both') {
        for (const s of statsB) if (types.includes(s.statType as any)) kept.push(s);
      }
    }

    // Use whichever record is official as the base GameState, swap stats
    const base = recordA.isRecord ? recordA.gameState : recordB.gameState;
    return {
      ...base,
      stats: kept,
      mergeOf: {
        recordAId: recordA.id,
        recordBId: recordB.id,
        sources: { ...sources },
        mergedAt: new Date().toISOString(),
      },
    };
  }

  async function handleSave() {
    setSaving(true);
    try {
      onSaveMerged(buildMerged());
    } finally {
      setSaving(false);
    }
  }

  const CATS = activeCategories;

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-3 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl animate-fade-in-up flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-indigo-500" />
            <h2 className="font-black text-slate-900 text-base">Compare Records</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Record headers */}
        <div className="grid grid-cols-[1fr_32px_1fr] gap-2 px-5 pt-4 pb-2 shrink-0">
          {[{ agg: aggA, rec: recordA, side: 'A' }, { agg: aggB, rec: recordB, side: 'B' }].map(({ agg, rec, side }, i) => (
            <React.Fragment key={side}>
              {i === 1 && <div />}
              <div className={`rounded-xl p-3 border-2 ${rec.isRecord ? 'border-yellow-400 bg-yellow-50' : 'border-slate-200 bg-slate-50'}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  {rec.isRecord && <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />}
                  <span className="text-[11px] font-black uppercase text-slate-500">Record {side}</span>
                </div>
                <div className="font-bold text-sm text-slate-800">{agg.source}</div>
                <div className="text-[11px] text-slate-400">{agg.date} · {agg.statCount} stats</div>
                {rec.submittedByName && <div className="text-[10px] text-slate-400 mt-0.5">by {rec.submittedByName}</div>}
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Score row */}
        <div className="grid grid-cols-[1fr_32px_1fr] gap-2 px-5 pb-3 shrink-0">
          {[aggA, aggB].map((agg, i) => (
            <React.Fragment key={i}>
              {i === 1 && (
                <div className="flex items-center justify-center">
                  <div className={`w-4 h-4 rounded-full ${scoreMatch ? 'bg-emerald-400' : 'bg-amber-400'}`} title={scoreMatch ? 'Scores match' : 'Scores differ'} />
                </div>
              )}
              <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                <div className="text-[10px] text-slate-400 font-bold uppercase mb-1">Score</div>
                <div className={`text-sm font-black ${scoreMatch ? 'text-slate-700' : 'text-amber-600'}`}>
                  {agg.score.ourSetsWon}–{agg.score.theirSetsWon} sets · {agg.score.ourScore}–{agg.score.theirScore}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Category sections */}
        <div className="overflow-y-auto flex-1 px-5 space-y-2 pb-2">
          {CATS.map(cat => {
            const expanded = expandedCats.has(cat);
            const hasA = categoryHasData(aggA, cat);
            const hasB = categoryHasData(aggB, cat);
            const src = sources[cat];

            // Determine alignment: sum all stat values across players per record
            const sumCat = (agg: AggregatedRecord) =>
              Object.values(agg.players).reduce((t, p) => t + categorySummary(p, cat).reduce((s, v) => s + v.value, 0), 0);
            const totalA = sumCat(aggA);
            const totalB = sumCat(aggB);
            const aligned = totalA === totalB;

            return (
              <div key={cat} className="border border-slate-200 rounded-xl overflow-hidden">
                {/* Category header */}
                <button
                  onClick={() => toggleExpand(cat)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${aligned ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                    <span className="font-bold text-sm text-slate-800">{CATEGORY_LABELS[cat]}</span>
                    {!hasA && <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">A: no data</span>}
                    {!hasB && <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">B: no data</span>}
                    {!aligned && (
                      <span className="text-[10px] text-amber-600 font-bold">Δ {Math.abs(totalA - totalB)}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Source selector */}
                    <div className="flex rounded-lg overflow-hidden border border-slate-200 text-[10px] font-bold" onClick={e => e.stopPropagation()}>
                      {(['a', 'b', 'both'] as const).map(s => (
                        <button
                          key={s}
                          onClick={() => setSource(cat, s)}
                          className={`px-2 py-1 transition-colors ${src === s
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                        >
                          {s === 'both' ? 'Both' : `Use ${s.toUpperCase()}`}
                        </button>
                      ))}
                    </div>
                    {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {/* Per-player rows */}
                {expanded && (
                  <div className="divide-y divide-slate-100">
                    {allPlayerIds
                      .filter(id => {
                        const pA = aggA.players[id];
                        const pB = aggB.players[id];
                        const aVals = pA ? categorySummary(pA, cat).reduce((s, v) => s + v.value, 0) : 0;
                        const bVals = pB ? categorySummary(pB, cat).reduce((s, v) => s + v.value, 0) : 0;
                        return aVals > 0 || bVals > 0;
                      })
                      .map(id => {
                        const pA = aggA.players[id] ?? EMPTY_PLAYER_STATS();
                        const pB = aggB.players[id] ?? EMPTY_PLAYER_STATS();
                        const vA = categorySummary(pA, cat);
                        const vB = categorySummary(pB, cat);
                        const rowAligned = vA.every((v, i) => v.value === vB[i].value);

                        return (
                          <div key={id} className={`grid grid-cols-[1fr_32px_1fr] gap-2 px-4 py-2 text-xs ${!rowAligned ? 'bg-amber-50/40' : ''}`}>
                            {/* Record A side */}
                            <div className={`flex items-center gap-2 ${src === 'b' ? 'opacity-40' : ''}`}>
                              <span className="font-semibold text-slate-700 truncate min-w-0 flex-1" title={playerName(id)}>{playerName(id)}</span>
                              <div className="flex gap-1.5 shrink-0">
                                {vA.map((v, i) => (
                                  <span key={i} className={`font-bold ${deltaClass(v.value, vB[i].value)}`}>
                                    {v.label} {v.value}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Delta column */}
                            <div className="flex flex-col items-center justify-center gap-0.5">
                              {vA.map((v, i) => <span key={i}><DeltaBadge a={v.value} b={vB[i].value} /></span>)}
                            </div>

                            {/* Record B side */}
                            <div className={`flex items-center gap-2 justify-end ${src === 'a' ? 'opacity-40' : ''}`}>
                              <div className="flex gap-1.5 shrink-0">
                                {vB.map((v, i) => (
                                  <span key={i} className={`font-bold ${deltaClass(v.value, vA[i].value)}`}>
                                    {v.label} {v.value}
                                  </span>
                                ))}
                              </div>
                              <span className="font-semibold text-slate-700 truncate min-w-0 flex-1 text-right" title={playerName(id)}>{playerName(id)}</span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend + Save */}
        <div className="px-5 pt-3 pb-5 border-t border-slate-100 shrink-0 space-y-3">
          <div className="flex items-center gap-4 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> Match</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Differ</span>
            <span className="text-slate-400">Select "Use A", "Use B", or "Both" per category, then save a merged record.</span>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-black rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" /> : <Check className="w-4 h-4" />}
            Save Merged Record
          </button>
        </div>
      </div>
    </div>
  );
}
